/**
 * Client-side downscale for MiniMenu image uploads.
 *
 * Evolution stores an upload byte-for-byte (`file.SaveAs`, no resize, no
 * re-encode) and, when a crop is saved, re-renders that crop at SOURCE
 * resolution on every request. So an untouched phone photo is what every diner
 * downloads — a 2505x3079 promo measured 1.3 MB on staging. Shrinking at the
 * source is the only lever the front end has until the API grows real
 * renditions.
 *
 * This MUST run before the crop editor: crop rectangles are stored in the
 * uploaded image's pixel space, so the editor has to measure the same image
 * that is ultimately uploaded.
 */

/**
 * Long edge cap, chosen for fidelity rather than the smallest possible file.
 *
 * What is served is a CROP of this image, so the cap has to leave the narrowest
 * frame enough pixels: the landing tablet-portrait frame is 1:3, so a full-height
 * crop of a 2800px image is ~930px wide against a pane that needs ~680px at 2x.
 * The widest case, a 9:16 mobile crop, lands at ~1575px against ~1290px needed on
 * a 3x phone. Both keep real headroom instead of matching exactly.
 */
export const UPLOAD_MAX_DIMENSION = 2800;
/**
 * JPEG quality. Measured against the live staging promo, 0.92 scores 44.3 dB PSNR
 * against a lossless render at the same size — past the ~40 dB point where JPEG
 * artefacts stop being visible. 0.95 costs a further ~25% in bytes for ~0.1 dB.
 */
export const UPLOAD_JPEG_QUALITY = 0.92;
/** What the API accepts (MiniMenuController rejects anything larger). */
export const UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
/** Refuse to decode beyond this — a huge file can stall or OOM the tab. */
export const UPLOAD_MAX_DECODE_BYTES = 30 * 1024 * 1024;

/** Already-small files are passed through: a second JPEG pass only loses quality. */
const SKIP_UNDER_BYTES = 300 * 1024;
/** GIF would lose its animation and SVG is vector — never rasterise either. */
const PASSTHROUGH_TYPES = ['image/gif', 'image/svg+xml'];
const ALPHA_CAPABLE_TYPES = ['image/png', 'image/webp'];

export interface DownscaleResult {
  /** The file to upload — the original when nothing could be gained. */
  file: File;
  changed: boolean;
  originalBytes: number;
  bytes: number;
}

export async function downscaleImageFile(
  file: File,
  maxDimension: number = UPLOAD_MAX_DIMENSION,
  quality: number = UPLOAD_JPEG_QUALITY
): Promise<DownscaleResult> {
  const originalBytes = file.size;
  const unchanged: DownscaleResult = { file, changed: false, originalBytes, bytes: originalBytes };

  if (!file.type?.startsWith('image/') ||
      PASSTHROUGH_TYPES.indexOf(file.type) >= 0 ||
      originalBytes > UPLOAD_MAX_DECODE_BYTES) {
    return unchanged;
  }

  let source: DecodedImage | null = null;
  try {
    source = await decode(file);
    const width = source.width;
    const height = source.height;
    if (!width || !height) {
      return unchanged;
    }

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    if (scale === 1 && originalBytes <= SKIP_UNDER_BYTES) {
      return unchanged;
    }

    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return unchanged;
    }
    // Keep the shipped resize path aligned with the fidelity measurements used
    // to choose the 2800px / q0.92 settings. Canvas otherwise defaults to its
    // browser-specific low-quality resampler.
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(source.image as CanvasImageSource, 0, 0, targetWidth, targetHeight);

    // A logo re-encoded as JPEG would gain a black box where its alpha was.
    const keepAlpha = ALPHA_CAPABLE_TYPES.indexOf(file.type) >= 0 &&
      hasTransparency(context, targetWidth, targetHeight);
    const type = keepAlpha ? 'image/png' : 'image/jpeg';
    const blob = await toBlob(canvas, type, quality);

    // PNG-encoding a screenshot-ish source can come out bigger than it went in.
    if (!blob || blob.size >= originalBytes) {
      return unchanged;
    }
    return {
      file: new File([blob], renameForType(file.name, type), { type, lastModified: Date.now() }),
      changed: true,
      originalBytes,
      bytes: blob.size
    };
  } catch {
    // Compression is an optimisation; never let it block an upload.
    return unchanged;
  } finally {
    source?.release();
  }
}

/** "3.1 MB → 412 KB" for the confirmation toast. */
export function describeDownscale(result: DownscaleResult): string {
  return `${formatBytes(result.originalBytes)} → ${formatBytes(result.bytes)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

interface DecodedImage {
  image: ImageBitmap | HTMLImageElement;
  width: number;
  height: number;
  release: () => void;
}

async function decode(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      // from-image bakes a phone photo's EXIF rotation into the pixels; without it
      // a portrait shot would upload sideways once stripped of its metadata.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return {
        image: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close?.()
      };
    } catch {
      // Safari < 15 and friends: fall through to the <img> path.
    }
  }
  return decodeViaImageElement(file);
}

function decodeViaImageElement(file: File): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({
      image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url)
    });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The image could not be decoded.'));
    };
    image.src = url;
  });
}

function hasTransparency(context: CanvasRenderingContext2D, width: number, height: number): boolean {
  const { data } = context.getImageData(0, 0, width, height);
  // We already paid the cost of reading the full pixel buffer. Check every
  // alpha byte so a small transparent cut-out cannot be missed and flattened
  // onto a JPEG background.
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }
  return false;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));
}

function renameForType(name: string, type: string): string {
  const extension = type === 'image/png' ? '.png' : '.jpg';
  const base = (name || 'image').replace(/\.[^.\\/]+$/, '');
  return `${base}${extension}`;
}
